import { IdToken, useAuth0 } from "@auth0/auth0-react";
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Message, useChat, type UseChatHelpers } from "ai/react";
import { useWorkspace } from "@/routes/simulator/context/wrokspace";
import { useInterval } from "@/hooks/useInterval";
import { params } from "@/params";
import {
  useSimulator,
  SimulatorWrapperContext,
} from "../../context/SimulatorWrapper";
import { useChat as useSimulatorChat } from "../../context/ChatWrapper";
import { useInstructionsNavigation } from "../../context/InstructionsNavigationContext";
import dayjs from "dayjs";

interface ImproveChatWrapperContextType {
  messages: Message[];
  input: string;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: () => void;
  setInput: (input: string) => void;
  append: (message: Message) => void;
  reload: () => void;
  setMessages: (messages: Message[]) => void;
  status: UseChatHelpers["status"];
  reset: () => void;
  improveChatId: string | null;
  topicId: number;
  releaseId: number | null;
  isDisabled: boolean;
  setIsDisabled: (disabled: boolean) => void;
}

const ImproveChatWrapperContext = createContext<
  ImproveChatWrapperContextType | undefined
>(undefined);

interface ImproveChatWrapperProps {
  topicId: number;
}

export function ImproveChatWrapper({
  children,
  topicId,
}: PropsWithChildren<ImproveChatWrapperProps>) {
  const { refreshReleaseData, currentRelease } = useSimulator();
  const { conversationId } = useSimulatorChat();
  const { workspace } = useWorkspace();
  const [tokenId, setToken] = useState<IdToken | undefined>(undefined);
  const { isAuthenticated, getIdTokenClaims } = useAuth0();

  const conversationIdRef = useRef<string | null>(null);
  const [improveChatId, setImproveChatId] = useState<string | null>(null);
  const sessionIdRef = useRef<string>(String(dayjs().valueOf()));
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const simulatorContext = useContext(SimulatorWrapperContext);
  const { improveTabResetSignal } = useInstructionsNavigation();

  useEffect(() => {
    (async () => {
      const token = await getIdTokenClaims();
      setToken(token);
    })();
  }, [isAuthenticated, getIdTokenClaims]);

  useInterval(() => {
    (async () => {
      if (isAuthenticated) {
        const token = await getIdTokenClaims();
        setToken(token);
      }
    })();
  }, 1000 * 60 * 10); // every 10 minutes

  const authToken = tokenId?.__raw ? tokenId.__raw : "";
  const headers: Record<string, string> = {
    Authorization: `Bearer ${authToken}`,
  };

  if (workspace) {
    headers["x-tenant-id"] = workspace;
  }

  const api = useMemo(() => {
    return `${params.API_URL}/platform/improve-chat/message?stream=true`;
  }, []);

  const bodyData = useMemo(
    () => ({
      id: sessionIdRef.current,
      tenantId: workspace,
      topicId: topicId,
      simulatorId: simulatorContext?.simulator?.id,
      improveChatId: improveChatId,
    }),
    [workspace, topicId, simulatorContext?.simulator?.id, improveChatId]
  );

  const ENABLE_MOCK_DATA = false;

  const mockMessages = useMemo(
    () =>
      ENABLE_MOCK_DATA
        ? [
            {
              id: "mock-user-1",
              role: "user" as const,
              content: "Can you help me improve the brief for this topic?",
              createdAt: new Date(),
            },
            {
              id: "mock-assistant-1",
              role: "assistant" as const,
              content: "I'll suggest an improved brief for you.",
              createdAt: new Date(),
              toolInvocations: [
                {
                  state: "result" as const,
                  toolCallId: "tool-1",
                  toolName: "suggest_brief_edit",
                  args: {
                    topicId: topicId,
                    mode: "replace",
                    anchor_text: "This topic handles customer inquiries.",
                    new_text:
                      "This topic handles customer inquiries with detailed responses that provide clear, actionable information to resolve customer questions effectively.",
                  },
                  result: "Brief suggestion prepared",
                },
              ],
            },
            {
              id: "mock-user-2",
              role: "user" as const,
              content: "Can you also add a new instruction?",
              createdAt: new Date(),
            },
            {
              id: "mock-assistant-2",
              role: "assistant" as const,
              content: "Here's a new instruction to add:",
              createdAt: new Date(),
              toolInvocations: [
                {
                  state: "result" as const,
                  toolCallId: "tool-2",
                  toolName: "suggest_instruction_add",
                  args: {
                    topicId: topicId,
                    content:
                      "Always greet customers warmly and professionally at the start of each interaction.",
                  },
                  result: "Instruction suggestion prepared",
                },
              ],
            },
            {
              id: "mock-user-3",
              role: "user" as const,
              content: "And a prerequisite too",
              createdAt: new Date(),
            },
            {
              id: "mock-assistant-thinking",
              role: "assistant" as const,
              content: "",
              createdAt: new Date(),
              toolInvocations: [
                {
                  state: "call" as const,
                  toolCallId: "tool-thinking",
                  toolName: "suggest_prerequisite_add",
                  args: {
                    topicId: topicId,
                    content: "",
                  },
                },
              ],
            },
            {
              id: "mock-assistant-3",
              role: "assistant" as const,
              content: "I'll add a prerequisite:",
              createdAt: new Date(),
              toolInvocations: [
                {
                  state: "result" as const,
                  toolCallId: "tool-3",
                  toolName: "suggest_prerequisite_add",
                  args: {
                    topicId: topicId,
                    content:
                      "Customer must be authenticated before accessing account information.",
                  },
                  result: "Prerequisite suggestion prepared",
                },
              ],
            },
          ]
        : [],
    [ENABLE_MOCK_DATA, topicId]
  );

  const {
    messages,
    input,
    handleInputChange,
    handleSubmit: originalHandleSubmit,
    setInput,
    append,
    reload,
    setMessages,
    status,
  } = useChat({
    id: sessionIdRef.current,
    api,
    headers,
    sendExtraMessageFields: true,
    body: bodyData,
    initialMessages: mockMessages,
    onResponse: (response: Response) => {
      const improveChatId = response.headers.get("x-improve-chat-id") ?? null;
      if (improveChatId) {
        conversationIdRef.current = improveChatId;
        setImproveChatId(improveChatId);
      }
    },
    onFinish: async () => {
      await refreshReleaseData();
    },
    onError: (error: Error) => {
      console.error("Improve chat error:", error);
    },
  });

  const handleSubmit = useCallback(
    (e?: React.FormEvent<HTMLFormElement>) => {
      if (!workspace || !topicId || !simulatorContext?.simulator?.id) {
        console.error("ImproveChat: Cannot submit - missing required fields");
        return;
      }

      originalHandleSubmit(e);
    },
    [originalHandleSubmit, workspace, topicId, simulatorContext?.simulator?.id]
  );

  const reset = useCallback(() => {
    sessionIdRef.current = String(dayjs().valueOf());
    conversationIdRef.current = null;
    setImproveChatId(null);
    setIsDisabled(false);
    // Reset to mock messages if enabled, otherwise clear all messages
    setMessages(mockMessages);
  }, [setMessages, mockMessages]);

  // Reset chat when topicId changes
  useEffect(() => {
    reset();
  }, [topicId, reset, conversationId]);

  useEffect(() => {
    if (!improveTabResetSignal) {
      return;
    }
    reset();
  }, [improveTabResetSignal, reset]);

  const value: ImproveChatWrapperContextType = {
    messages,
    input,
    handleInputChange,
    handleSubmit,
    append,
    reload,
    setMessages,
    status,
    setInput,
    reset,
    improveChatId: improveChatId?.toString() ?? null,
    topicId,
    releaseId: currentRelease?.id ?? null,
    isDisabled,
    setIsDisabled,
  };

  return (
    <ImproveChatWrapperContext.Provider value={value}>
      {children}
    </ImproveChatWrapperContext.Provider>
  );
}

export function useImproveChat(): ImproveChatWrapperContextType {
  const context = useContext(ImproveChatWrapperContext);
  if (context === undefined) {
    throw new Error("useImproveChat must be used within an ImproveChatWrapper");
  }
  return context;
}
